import '../communityChatRoom.scss';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Skeleton,
  Typography,
} from 'antd';
import moment from 'moment';
import { PiPushPin } from 'react-icons/pi';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import CommunityMessagesAPI from '../../../api/CommunityMessageAPI';
import { SocketAPI } from '../../../api/SocketAPI';
import Header from '../../../components/Header/Header';
import {
  getMessageRoomChats,
  initialChatState,
  markChannelLastMessageSeen,
  setActiveUserInSelectedRoom,
  setChatRoomDetails,
  setTouchedMessageData,
  updateChatState,
} from '../../../context/communityMessageReducer';
import { loadMentionedUsers } from '../../../shared/communityUtils';
import showAppError from '../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../shared/hooks';
import {
  getDMUser,
  getReadableDateFormat,
} from '../../../shared/utils';
import { ICommunityMessage } from '../../../types/communityMessagesTypes';
import { SOCKET_ACTION_EVENTS } from '../../../types/socketTypes';
import BidirectionalScroll, {
  BidirectionalScrollRef,
} from '../../Messages/components/BidirectionalScroll';
import CommunityChatbackgroundLayer from './CommunityChatbackgroundLayer';
import CommunityChatScrollTobuttomButton
  from './CommunityChatScrollTobuttomButton';
import CommunityInputWithEmojiPicker from './CommunityInputWithEmojiPicker';
import CreatorTag from './CreatorTag';
import InfoMessageModel from './InfoMessageModel';
import MessagesCard from './MessagesCard';
import PinnedMessageList from './PinnedMessageList';
import ReplyThreadModel from './ReplyThreadModel';
import TouchedMode from './TouchedMode';

type Props = {
  channelRoomId?: string;
  hideHeader?: boolean;
  isCommunityChannelPage?: boolean;
};

const CommunityChatRoom: React.FC<Props> = ({
  channelRoomId,
  hideHeader,
  isCommunityChannelPage,
}) => {
  const navigate = useAppNavigate();
  const rootNavigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { roomId } = useParams();

  const { chatRoomDetails, chatsState } = useAppSelector(
    (state) => state?.communityMessage,
  );
  const { hostMetadata } = useAppSelector((state) => state?.app);

  const [loading, setLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<BidirectionalScrollRef>(null);
  const [showPinnedMessage, setShowPinnedMessage] = useState(false);
  const [selectMessageForInfo, setSelectMessageForInfo] = useState<
    string | null
  >(null);
  const [isReplyInfoOpen, setIsReplyInfoOpen] = useState(false);
  // const [additionaClass, setAdditionaClass] = useState<string>('default');
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [isOnlyReaction, setIsOnlyReaction] = useState(false);
  const [preSelectedReaction, setPreSelectedReaction] = useState<string | null>(
    null,
  );

  const handleScrollToBottom = () => {
    scrollRef.current?.scrollToBottom();
    setBadgeCount(0);
    const chatList = [...chatsState?.chats].map((chat) => ({
      ...chat,
      isNewMessage: false,
    }));
    dispatch(updateChatState({ chats: chatList }));
    dispatch(markChannelLastMessageSeen(chatsState?.chats[0]?._id));
  };

  const handleBack = () => {
    setTimeout(() => {
      navigate.goBack();
    }, 200);
  };

  const fetchMessageList = useCallback(async () => {
    if (chatsState?.hasMore && !chatsState?.loading && chatRoomDetails?._id) {
      const messageContainer = document.getElementById('bidirectional-scroll');
      const scrollHeightBefore = messageContainer?.scrollHeight || 0;
      const scrollTopBefore = messageContainer?.scrollTop || 0;

      // After DOM update
      const needToStayBotton = chatsState?.chats?.length === 0 ? true : false;
      await dispatch(
        getMessageRoomChats(chatsState?.chats, chatRoomDetails?._id),
      );
      if (needToStayBotton) {
        scrollRef.current?.scrollToBottom();
      } else {
        setTimeout(() => {
          if (messageContainer) {
            const scrollHeightAfter = messageContainer.scrollHeight;
            messageContainer.scrollTop =
              scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
          }
        }, 0);
      }
    }
  }, [
    chatRoomDetails?._id,
    chatsState?.chats,
    chatsState?.hasMore,
    chatsState?.loading,
    dispatch,
  ]);

  const listData = useMemo(() => {
    const listItems: {
      type: 'message' | 'date' | 'new-participant';
      message?: ICommunityMessage;
      date?: Date;
      participantDetails?: { participantName: string; participantId: string };
    }[] = [];
    if (chatsState?.chats?.length === 0) return [];
    chatsState?.chats.forEach((item, index) => {
      if (index === 0) {
        listItems.push({ type: 'date', date: new Date(item.createdAt) });
      } else if (
        !moment(item?.createdAt).isSame(
          chatsState?.chats[index - 1].createdAt,
          'day',
        )
      ) {
        listItems.push({
          type: 'date',
          date: new Date(item.createdAt),
        });
      }
      listItems.push({ type: 'message', message: item });
    });
    // listItemLength.current = listItems.length;
    return listItems;
  }, [chatsState?.chats]);

  const displayMessages = useCallback(() => {
    return (
      <BidirectionalScroll
        ref={scrollRef}
        className={`messageList`}
        onShowScrollButton={(show) => {
          // console.log('show scroll down');
          // dispatch(setShowScrollDown(show));
          setShowScrollDown(show);
        }}
        onStartReached={() => {
          fetchMessageList();
        }}
        onEndReached={() => {
          // setIsAtBottom(true);
        }}>
        <>
          {listData.map((eachData) => {
            if (eachData?.type === 'message' && eachData?.message) {
              return (
                <MessagesCard
                  setSelectMessageForInfo={setSelectMessageForInfo}
                  message={eachData?.message}
                  setIsOnlyReaction={setIsOnlyReaction}
                  setPreSelectedReaction={setPreSelectedReaction}
                />
              );
            }
            if (eachData?.type === 'date' && eachData?.date) {
              return (
                // need to add sticky in future using this class -> chat-date-sticky
                <div
                  className="messageList__dateContainer" //chat-date-sticky
                  key={eachData?.date.getTime()}>
                  <Typography.Text className="messageList__date">
                    {getReadableDateFormat(eachData?.date).toUpperCase()}
                  </Typography.Text>
                </div>
              );
            }
            return null;
          })}
        </>
      </BidirectionalScroll>
    );
  }, [listData, fetchMessageList]);

  const handleChannelTitleClick = useCallback(() => {
    const searchParams = new URLSearchParams(location?.search);
    searchParams.append('info', 'true');
    rootNavigate(`${location?.pathname}?${searchParams?.toString()}`);
  }, [location?.pathname, location?.search, rootNavigate]);

  const displayMessagesOrLoader = () => {
    if (loading) {
      return (
        <>
          {Array(10)
            .fill(1)
            .map(() => (
              <Skeleton avatar paragraph={{ rows: 0 }} />
            ))}
        </>
      );
    }
    return displayMessages();
  };

  const dmUser = useMemo(
    () =>
      chatRoomDetails?.roomType === 'dm' && chatRoomDetails?._id
        ? getDMUser(chatRoomDetails)
        : null,
    [chatRoomDetails],
  );

  const randerAvatar = useMemo(() => {
    if (chatRoomDetails?.roomType === 'dm') {
      return (
        <div className="chat-room-header_dm-avatar">
          <img
            src={
              dmUser?.profilePicUrl ||
              'https://tagmango.com/staticassets/avatar-placeholder.png-1612857612139.png'
            }
            alt="logo"
          />
        </div>
      );
    }
    if (
      chatRoomDetails?.channel?.communityProfilePicture &&
      chatRoomDetails?.channel?.communityProfilePictureType === 'emoji'
    ) {
      return (
        <div className="chat-room-header_emoji-wrapper">
          {chatRoomDetails?.channel?.communityProfilePicture}
        </div>
      );
    }
    return (
      <div className="chat-room-header_logo-wrapper">
        <img
          src={
            chatRoomDetails?.channel?.communityProfilePicture ||
            'https://tagmango.com/staticassets/avatar-placeholder.png-1612857612139.png'
          }
          alt="logo"
        />
      </div>
    );
  }, [
    chatRoomDetails?.channel?.communityProfilePicture,
    chatRoomDetails?.channel?.communityProfilePictureType,
    chatRoomDetails?.roomType,
    dmUser?.profilePicUrl,
  ]);

  const customTitle = useMemo(() => {
    return (
      <button
        className="chat-room-header"
        onClick={() => handleChannelTitleClick()}>
        {randerAvatar}
        <div className="chat-room-header_text-wrapper">
          <div style={{ display: 'flex' }}>
            <p className="chat-room-header_text-wrapper_title">
              {dmUser?.name || chatRoomDetails?.channel?.name}
            </p>
            {hostMetadata?.creator?._id === dmUser?._id ? <CreatorTag /> : null}
          </div>
          <span className="chat-room-header_text-wrapper_subtitle">
            Tap here for info
          </span>
        </div>
      </button>
    );
  }, [
    chatRoomDetails?.channel?.name,
    dmUser?._id,
    dmUser?.name,
    handleChannelTitleClick,
    hostMetadata?.creator?._id,
    randerAvatar,
  ]);

  const joinRoom = useCallback(
    (room: string) => {
      SocketAPI.emit(SOCKET_ACTION_EVENTS.ROOM_JOIN, { room: room }, (resp) => {
        dispatch(
          setActiveUserInSelectedRoom([
            ...(resp.result.activeParticipants || []),
          ]),
        );
      });
    },
    [dispatch],
  );

  // const setAddinationalClass = useCallback(
  //   (data: { [key: string]: boolean }) => {
  //     if (data?.files && data?.mediaCard) {
  //       setAdditionaClass('filesMediaCard');
  //     } else if (data?.files) {
  //       setAdditionaClass('files');
  //     } else if (data?.mediaCard) {
  //       setAdditionaClass('mediaCard');
  //     } else {
  //       setAdditionaClass('default');
  //     }
  //   },
  //   [],
  // );

  const getRoomDetails = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        const resp = await CommunityMessagesAPI.getCommuiniyMessageRoomDetails(
          id,
        );
        if (resp?.status === 200) {
          dispatch(setChatRoomDetails(resp?.data?.result));
          joinRoom(resp?.data?.result?._id);
          // dispatch(
          //   updateChatList(
          //     resp?.data?.result?.lastMessage
          //       ? [resp?.data?.result?.lastMessage]
          //       : [],
          //   ),
          // );
        } else {
          showAppError(resp.data);
        }
      } catch (error) {
        showAppError(error);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, joinRoom],
  );

  const handleChannelLastMessageSeen = useCallback(() => {
    if (chatRoomDetails?._id && chatRoomDetails?.lastMessage?._id) {
      dispatch(markChannelLastMessageSeen(chatRoomDetails?.lastMessage?._id));
    }
  }, [chatRoomDetails?._id, chatRoomDetails?.lastMessage?._id, dispatch]);

  useEffect(() => {
    if (chatRoomDetails?._id && !chatsState?.loading && chatsState?.hasMore) {
      fetchMessageList();
    }
    handleChannelLastMessageSeen();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRoomDetails, handleChannelLastMessageSeen]);

  useEffect(() => {
    if (roomId || channelRoomId) {
      getRoomDetails(roomId || (channelRoomId as string));
    }
  }, [getRoomDetails, roomId, channelRoomId]);

  useEffect(() => {
    return () => {
      dispatch(setChatRoomDetails(null));
      dispatch(updateChatState(initialChatState));
      dispatch(setTouchedMessageData(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (chatRoomDetails?._id) {
      if (showScrollDown) {
        setBadgeCount(
          chatsState?.chats?.filter((item) => item?.isNewMessage).length,
        );
      } else {
        const chatList = [...chatsState?.chats];
        const lastMessage = chatList[chatList?.length - 1];
        if (lastMessage?.isNewMessage) {
          dispatch(markChannelLastMessageSeen(lastMessage?._id));
          const messageCard = document.getElementById(lastMessage?._id);
          if (messageCard) {
            messageCard.scrollIntoView({ behavior: 'smooth' });
          }
          setBadgeCount(0);
          const updatedChatList = chatList.map((chat) => ({
            ...chat,
            isNewMessage: false,
          }));
          dispatch(updateChatState({ chats: updatedChatList }));
        }
      }
    }
  }, [chatRoomDetails?._id, chatsState?.chats, dispatch, showScrollDown]);

  return (
    <div
      className={`${
        isCommunityChannelPage ? 'channel-chat-room' : ''
      } chat-room_container `}>
      {!hideHeader ? (
        <Header
          title={dmUser?.name || (chatRoomDetails?.channel?.name as string)}
          customTitle={customTitle}
          handleBack={handleBack}
          customHeaderClass="chat-room-header-wrapper"
          actionItems={
            chatRoomDetails?.pinnedMessages?.length
              ? [
                  <button
                    className="chat-room-pinned"
                    onClick={() => {
                      const searchParams = new URLSearchParams();
                      searchParams?.append('pinnedMessage', 'true');
                      rootNavigate({ search: searchParams?.toString() });
                      setShowPinnedMessage(true);
                    }}>
                    <PiPushPin /> {chatRoomDetails?.pinnedMessages?.length}
                  </button>,
                ]
              : []
          }
        />
      ) : null}
      <div
        className={`chat-room_container_wrapper  `}
        ref={(ref) => (scrollContainerRef.current = ref)}
        id="chat-room_container_wrapper_1234">
        {displayMessagesOrLoader()}
        <CommunityInputWithEmojiPicker
          // setFilesAndMediaCardStatus={setAddinationalClass}
          searchMentionedUser={
            chatRoomDetails?.channel
              ? (search: string) =>
                  loadMentionedUsers(chatRoomDetails?.channel?._id, search)
              : undefined
          }
        />
      </div>
      <TouchedMode setInfoMessageId={(id) => setSelectMessageForInfo(id)} />
      <PinnedMessageList
        showModal={showPinnedMessage}
        closeModal={() => {
          setShowPinnedMessage(false);
          navigate.goBack();
        }}
      />
      <InfoMessageModel
        roomId={chatRoomDetails?._id as string}
        messageId={selectMessageForInfo}
        isOnlyReaction={isOnlyReaction}
        closeModal={() => {
          setSelectMessageForInfo(null);
          setIsOnlyReaction(false);
          setIsReplyInfoOpen(false);
          setShowPinnedMessage(false);
        }}
        preSelectedReaction={preSelectedReaction}
        isReplyInfoOpen={isReplyInfoOpen}
      />
      <ReplyThreadModel
        setSelectMessageForInfo={setSelectMessageForInfo}
        setIsOnlyReaction={setIsOnlyReaction}
        setPreSelectedReaction={setPreSelectedReaction}
        setIsReplyInfoOpen={setIsReplyInfoOpen}
      />
      <CommunityChatbackgroundLayer />
      <CommunityChatScrollTobuttomButton
        badgeCount={badgeCount}
        showScrollDown={showScrollDown}
        handleScrollToBottom={handleScrollToBottom}
      />
    </div>
  );
};

export default CommunityChatRoom;
